import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { authService } from '../services/authService';

const Login: React.FC = () => {
    const navigate = useNavigate();
    const { login, user } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [zamanUyariModal, setZamanUyariModal] = useState(false);

    useEffect(() => {
        authService.checkAuthState();
        if (user) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    const formik = useFormik({
        initialValues: {
            Email: '',
            Sifre: ''
        },
        validationSchema: Yup.object({
            Email: Yup.string()
                .email('Geçerli bir e-posta adresi giriniz')
                .required('E-posta adresi zorunludur'),
            Sifre: Yup.string()
                .required('Şifre zorunludur')
        }),
        onSubmit: async (values) => {
            try {
                await login(values.Email, values.Sifre);
                authService.checkAuthState();
                navigate('/dashboard');
                toast.success('Giriş başarılı!');
            } catch (error: any) {
                console.error('Login error:', error);
                const msg = error?.response?.data?.message ?? '';
                if (msg.includes('zaman') || msg.includes('saatte')) {
                    setZamanUyariModal(true);
                } else {
                    toast.error('E-posta veya şifre hatalı!');
                }
            }
        },
    });

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

                *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

                :root {
                    --orange:       #cd6906;
                    --orange-hover: #b85d05;
                    --orange-light: rgba(205,105,6,0.12);
                    --orange-glow:  rgba(205,105,6,0.35);
                    --dark:         #151515;
                    --dark-card:    #1a1a1a;
                    --dark-border:  #2a2a2a;
                    --muted:        #888888;
                    --muted-light:  #aaaaaa;
                    --white:        #ffffff;
                    --input-bg:     #f7f7f7;
                    --input-border: #e4e4e4;
                    --text:         #1a1a1a;
                    --text-light:   #555555;
                    --danger:       #e74c3c;
                }

                html, body { height: 100%; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }

                /* ── LAYOUT ── */
                .lg-root {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    height: 100vh;
                    max-height: 100vh;
                    overflow: hidden;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                /* ── LEFT PANEL ── */
                .lg-left {
                    background: var(--dark);
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    padding: 40px 56px;
                    overflow: hidden;
                    height: 100vh;
                }
                .lg-left::before {
                    content: '';
                    position: absolute;
                    top: -120px; left: -120px;
                    width: 480px; height: 480px;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(205,105,6,0.18) 0%, transparent 70%);
                    pointer-events: none;
                }
                .lg-left::after {
                    content: '';
                    position: absolute;
                    bottom: -80px; right: -80px;
                    width: 360px; height: 360px;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(205,105,6,0.10) 0%, transparent 70%);
                    pointer-events: none;
                }
                .lg-grid-bg {
                    position: absolute;
                    inset: 0;
                    background-image:
                        linear-gradient(rgba(205,105,6,0.04) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(205,105,6,0.04) 1px, transparent 1px);
                    background-size: 40px 40px;
                    pointer-events: none;
                }
                .lg-brand-logo img {
                    height: 52px;
                    width: auto;
                    object-fit: contain;
                }
                .lg-hero {
                    position: relative;
                    z-index: 2;
                }
                .lg-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: var(--orange-light);
                    border: 1px solid rgba(205,105,6,0.3);
                    color: var(--orange);
                    font-size: 12px;
                    font-weight: 600;
                    padding: 6px 14px;
                    border-radius: 100px;
                    margin-bottom: 20px;
                    letter-spacing: 0.2px;
                }
                .lg-badge-dot {
                    width: 6px; height: 6px;
                    border-radius: 50%;
                    background: var(--orange);
                    animation: lg-pulse 2s ease-in-out infinite;
                }
                @keyframes lg-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%       { opacity: 0.5; transform: scale(0.8); }
                }
                .lg-headline {
                    font-size: 36px;
                    font-weight: 900;
                    line-height: 1.1;
                    color: var(--white);
                    letter-spacing: -1px;
                    margin-bottom: 16px;
                }
                .lg-headline .accent { color: var(--orange); }
                .lg-desc {
                    font-size: 16px;
                    color: var(--muted-light);
                    line-height: 1.7;
                    max-width: 380px;
                }

                /* ── RIGHT PANEL ── */
                .lg-right {
                    background: var(--white);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 24px 56px;
                    position: relative;
                    height: 100vh;
                    overflow-y: auto;
                }
                .lg-form-wrapper {
                    width: 100%;
                    max-width: 400px;
                }

                /* Header */
                .lg-logo-area {
                    text-align: center;
                    margin-bottom: 20px;
                }
                .lg-login-icon {
                    width: 64px; height: 64px;
                    background: var(--orange-light);
                    border: 2px solid rgba(205,105,6,0.2);
                    border-radius: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px;
                    overflow: hidden;
                }
                .lg-login-icon img {
                    width: 44px; height: 44px;
                    object-fit: contain;
                }
                .lg-welcome-title {
                    font-size: 24px;
                    font-weight: 800;
                    color: var(--text);
                    letter-spacing: -0.5px;
                    margin-bottom: 6px;
                }
                .lg-welcome-sub {
                    font-size: 13px;
                    color: var(--text-light);
                    line-height: 1.5;
                }

                /* Form */
                .lg-form { display: flex; flex-direction: column; gap: 0; }
                .lg-field { margin-bottom: 14px; }
                .lg-label {
                    display: block;
                    font-size: 13px;
                    font-weight: 600;
                    color: var(--text);
                    margin-bottom: 8px;
                    letter-spacing: 0.1px;
                }
                .lg-input-wrap { position: relative; }
                .lg-input-icon {
                    position: absolute;
                    left: 16px; top: 50%;
                    transform: translateY(-50%);
                    width: 16px; height: 16px;
                    color: var(--muted);
                    pointer-events: none;
                    display: flex; align-items: center;
                    transition: color 0.2s;
                    z-index: 1;
                }
                .lg-input {
                    width: 100%;
                    padding: 11px 16px 11px 46px;
                    background: var(--input-bg);
                    border: 1.5px solid var(--input-border);
                    border-radius: 12px;
                    font-size: 14px;
                    font-family: 'Inter', sans-serif;
                    color: var(--text);
                    transition: all 0.2s;
                    outline: none;
                }
                .lg-input::placeholder { color: #bbb; }
                .lg-input:focus {
                    background: var(--white);
                    border-color: var(--orange);
                    box-shadow: 0 0 0 4px var(--orange-light);
                }
                .lg-input-wrap:focus-within .lg-input-icon { color: var(--orange); }
                .lg-input.lg-input-error { border-color: var(--danger); background: #fef2f2; }
                .lg-input.lg-input-error:focus { box-shadow: 0 0 0 4px rgba(231,76,60,0.10); }
                .lg-error-msg {
                    font-size: 11.5px;
                    font-weight: 500;
                    color: var(--danger);
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    margin-top: 6px;
                }
                .lg-eye-btn {
                    position: absolute;
                    right: 14px; top: 50%;
                    transform: translateY(-50%);
                    background: none; border: none;
                    cursor: pointer;
                    color: var(--muted);
                    padding: 4px;
                    display: flex; align-items: center;
                    transition: color 0.2s;
                    z-index: 1;
                }
                .lg-eye-btn:hover { color: var(--orange); }

                /* Remember + Forgot */
                .lg-row-between {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }
                .lg-checkbox-wrap {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    user-select: none;
                }
                .lg-checkbox {
                    width: 16px; height: 16px;
                    accent-color: var(--orange);
                    cursor: pointer;
                }
                .lg-checkbox-label {
                    font-size: 13px;
                    color: var(--text-light);
                }
                .lg-forgot {
                    font-size: 13px;
                    font-weight: 600;
                    color: var(--orange);
                    text-decoration: none;
                    cursor: pointer;
                    transition: opacity 0.2s;
                    background: none; border: none;
                }
                .lg-forgot:hover { opacity: 0.75; }

                /* Submit button */
                .lg-submit-btn {
                    width: 100%;
                    padding: 13px;
                    background: var(--orange);
                    color: var(--white);
                    border: none;
                    border-radius: 12px;
                    font-size: 15px;
                    font-weight: 700;
                    font-family: 'Inter', sans-serif;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: all 0.25s;
                    letter-spacing: 0.2px;
                    position: relative;
                    overflow: hidden;
                    margin-bottom: 16px;
                }
                .lg-submit-btn:hover {
                    background: var(--orange-hover);
                    box-shadow: 0 8px 24px var(--orange-glow);
                    transform: translateY(-1px);
                }
                .lg-submit-btn:active { transform: translateY(0); box-shadow: none; }
                .lg-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; box-shadow: none; }
                .lg-submit-btn svg { transition: transform 0.2s; flex-shrink: 0; }
                .lg-submit-btn:hover svg { transform: translateX(3px); }

                .lg-spinner {
                    width: 18px; height: 18px;
                    border: 2.5px solid rgba(255,255,255,0.4);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: lg-spin 0.7s linear infinite;
                }
                @keyframes lg-spin { to { transform: rotate(360deg); } }

                .lg-divider {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin: 0 0 12px;
                    color: #ccc;
                    font-size: 12px;
                }
                .lg-divider::before, .lg-divider::after {
                    content: '';
                    flex: 1;
                    height: 1px;
                    background: var(--input-border);
                }

                .lg-demo-link {
                    text-align: center;
                    font-size: 13px;
                    color: var(--text-light);
                    margin-bottom: 14px;
                }
                .lg-demo-link a {
                    font-weight: 600;
                    color: var(--orange);
                    text-decoration: none;
                    transition: opacity 0.2s;
                }
                .lg-demo-link a:hover { opacity: 0.75; }

                .lg-security {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    font-size: 12px;
                    color: #bbb;
                    margin-bottom: 16px;
                }

                .lg-footer {
                    text-align: center;
                    padding-top: 14px;
                    border-top: 1px solid var(--input-border);
                    font-size: 12px;
                    color: #aaa;
                }
                .lg-footer a { color: #aaa; text-decoration: none; }
                .lg-footer a:hover { color: var(--orange); }

                /* ── RESPONSIVE ── */
                @media (max-width: 900px) {
                    .lg-root { grid-template-columns: 1fr; height: auto; max-height: none; overflow: visible; }
                    .lg-left { display: none; }
                    .lg-right { padding: 32px 24px; height: auto; }
                    .lg-form-wrapper { max-width: 440px; }
                }

                /* ── ZAMAN UYARI MODAL ── */
                .lg-zaman-overlay {
                    position: fixed; inset: 0;
                    background: rgba(15,23,42,0.55);
                    backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 9999; padding: 20px;
                    animation: lgFadeIn 0.2s ease;
                }
                @keyframes lgFadeIn { from { opacity: 0; } to { opacity: 1; } }
                .lg-zaman-modal {
                    background: #fff;
                    border-radius: 20px;
                    max-width: 420px; width: 100%;
                    box-shadow: 0 24px 64px rgba(15,23,42,0.30);
                    overflow: hidden;
                    animation: lgSlideUp 0.22s ease;
                }
                @keyframes lgSlideUp {
                    from { opacity: 0; transform: translateY(16px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .lg-zaman-modal-top {
                    background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
                    padding: 28px 28px 20px;
                    text-align: center;
                    border-bottom: 1px solid #fed7aa;
                }
                .lg-zaman-modal-icon {
                    width: 64px; height: 64px;
                    background: #fff;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 14px;
                    box-shadow: 0 4px 16px rgba(205,105,6,0.18);
                }
                .lg-zaman-modal-title {
                    font-size: 17px; font-weight: 800;
                    color: #92400e; letter-spacing: -0.3px;
                    margin-bottom: 6px;
                }
                .lg-zaman-modal-sub {
                    font-size: 13px; color: #b45309; line-height: 1.5;
                }
                .lg-zaman-modal-body {
                    padding: 20px 28px 24px;
                    text-align: center;
                }
                .lg-zaman-modal-desc {
                    font-size: 14px; color: #374151; line-height: 1.65;
                    margin-bottom: 20px;
                }
                .lg-zaman-modal-btn {
                    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
                    padding: 11px 28px;
                    background: var(--orange); color: #fff;
                    border: none; border-radius: 12px;
                    font-size: 14px; font-weight: 700; font-family: 'Inter', sans-serif;
                    cursor: pointer;
                    box-shadow: 0 4px 14px rgba(205,105,6,0.30);
                    transition: background 0.15s, transform 0.1s;
                }
                .lg-zaman-modal-btn:hover { background: var(--orange-hover); transform: translateY(-1px); }
            `}</style>

            <div className="lg-root">

                {/* ══════════════════ LEFT PANEL ══════════════════ */}
                <div className="lg-left">
                    <div className="lg-grid-bg" />

                    {/* Logo */}
                    <div className="lg-brand-logo" style={{ position: 'relative', zIndex: 2 }}>
                        <img src="/logo/yapıcrm_logo_beyaz.png" alt="Yapı CRM" />
                    </div>

                    {/* Hero */}
                    <div className="lg-hero">
                        <div className="lg-badge">
                            <span className="lg-badge-dot" />
                            Yeni Nesil CRM Platformu
                        </div>
                        <h1 className="lg-headline">
                            Yapı Projelerinizin<br />
                            <span className="accent">Dijital Yönetim</span><br />
                            Merkezi
                        </h1>
                        <p className="lg-desc">
                            Projelerinizi tek bir platformdan yönetin. Satış, sözleşme,
                            ödeme takibi ve daha fazlası için güvenilir çözüm.
                        </p>
                    </div>

                    <div></div>
                </div>

                {/* ══════════════════ RIGHT PANEL ═════════════════ */}
                <div className="lg-right">
                    <div className="lg-form-wrapper">

                        {/* Header */}
                        <div className="lg-logo-area">
                            <div className="lg-login-icon">
                                <img src="/logo/yapıcrm_icon_transparan.png" alt="Yapı CRM" />
                            </div>
                            <div className="lg-welcome-title">Hoş Geldiniz</div>
                            <div className="lg-welcome-sub">Devam etmek için hesabınıza giriş yapın</div>
                        </div>

                        {/* Form */}
                        <form className="lg-form" onSubmit={formik.handleSubmit} noValidate>

                            {/* Email */}
                            <div className="lg-field">
                                <label className="lg-label">E-posta Adresi</label>
                                <div className="lg-input-wrap">
                                    <span className="lg-input-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                            <polyline points="22,6 12,13 2,6"/>
                                        </svg>
                                    </span>
                                    <input
                                        className={`lg-input${formik.touched.Email && formik.errors.Email ? ' lg-input-error' : ''}`}
                                        type="email"
                                        placeholder="ornek@sirket.com"
                                        autoComplete="email"
                                        {...formik.getFieldProps('Email')}
                                    />
                                </div>
                                {formik.touched.Email && formik.errors.Email && (
                                    <div className="lg-error-msg">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                                        {formik.errors.Email}
                                    </div>
                                )}
                            </div>

                            {/* Password */}
                            <div className="lg-field">
                                <label className="lg-label">Şifre</label>
                                <div className="lg-input-wrap">
                                    <span className="lg-input-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                        </svg>
                                    </span>
                                    <input
                                        className={`lg-input${formik.touched.Sifre && formik.errors.Sifre ? ' lg-input-error' : ''}`}
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                        style={{ paddingRight: 48 }}
                                        {...formik.getFieldProps('Sifre')}
                                    />
                                    <button
                                        type="button"
                                        className="lg-eye-btn"
                                        onClick={() => setShowPassword(v => !v)}
                                        tabIndex={-1}
                                        aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                                    >
                                        {showPassword ? (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="1" y1="1" x2="23" y2="23"/>
                                                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 7 11 7a13.16 13.16 0 0 1-1.67 2.68M6.72 6.72A10 10 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.27-1.49"/>
                                                <path d="M9 9a3 3 0 0 0 4.24 4.24"/>
                                            </svg>
                                        ) : (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                <circle cx="12" cy="12" r="3"/>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {formik.touched.Sifre && formik.errors.Sifre && (
                                    <div className="lg-error-msg">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                                        {formik.errors.Sifre}
                                    </div>
                                )}
                            </div>

                            {/* Remember & Forgot */}
                            <div className="lg-row-between">
                                <label className="lg-checkbox-wrap">
                                    <input
                                        type="checkbox"
                                        className="lg-checkbox"
                                        checked={rememberMe}
                                        onChange={() => setRememberMe(v => !v)}
                                    />
                                    <span className="lg-checkbox-label">Beni Hatırla</span>
                                </label>
                                <button type="button" className="lg-forgot">Şifremi Unuttum</button>
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                className="lg-submit-btn"
                                disabled={formik.isSubmitting}
                            >
                                {formik.isSubmitting ? (
                                    <span className="lg-spinner" />
                                ) : (
                                    <>
                                        <span>Giriş Yap</span>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="5" y1="12" x2="19" y2="12"/>
                                            <polyline points="12 5 19 12 12 19"/>
                                        </svg>
                                    </>
                                )}
                            </button>
                        </form>

                        <div className="lg-divider">veya</div>

                        <div className="lg-demo-link">
                            <span>Hesabınız yok mu? </span>
                            <a href="https://yapicrm.com/demo-talep-et/" target="_blank" rel="noreferrer">Demo Talep Edin →</a>
                        </div>

                        {/* Security note */}
                        <div className="lg-security">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            </svg>
                            256-bit SSL ile korunmaktadır
                        </div>

                        {/* Footer */}
                        <div className="lg-footer">
                            &copy; 2026 Yapı CRM &middot; Tüm hakları saklıdır &nbsp;&middot;&nbsp;
                            <a target='_blank' href="https://yapicrm.com/kullanim-sartlari/">Gizlilik</a>
                            &nbsp;&middot;&nbsp;
                            <a target='_blank' href="https://yapicrm.com/kullanim-sartlari/">Kullanım Şartları</a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Zaman Kısıtlaması Uyarı Modalı */}
            {zamanUyariModal && (
                <div className="lg-zaman-overlay">
                    <div className="lg-zaman-modal">
                        <div className="lg-zaman-modal-top">
                            <div className="lg-zaman-modal-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cd6906" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"/>
                                    <polyline points="12 6 12 12 16 14"/>
                                </svg>
                            </div>
                            <div className="lg-zaman-modal-title">Giriş Saatleri Dışında</div>
                            <div className="lg-zaman-modal-sub">Şu an sisteme giriş yapma yetkiniz bulunmuyor</div>
                        </div>
                        <div className="lg-zaman-modal-body">
                            <p className="lg-zaman-modal-desc">
                                Hesabınıza yönetici tarafından <strong>giriş zaman kısıtlaması</strong> uygulanmıştır.<br/>
                                Lütfen izin verilen gün ve saatlerde tekrar deneyiniz ya da yöneticiniz ile iletişime geçiniz.
                            </p>
                            <button className="lg-zaman-modal-btn" onClick={() => setZamanUyariModal(false)}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                Anladım
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Login;
